/**
 * Plain CSS text the settings components render in a `<style>`, for the reason
 * `access-setup-screen.styles.ts` gives: this package ships source with no Sass
 * step. The DS exposes its tokens only as Sass functions, so each value is the
 * resolved token, named beside it.
 */
export const SETTINGS_CSS = `
.ui-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.ui-settings-card,
.ui-settings-card > ps-card {
  display: block;
  width: 100%;
}

/* The overridden rule sits inside the DS card's own border and radius. */
.ui-settings-card--over > ps-card::part(card-body) {
  box-shadow: inset 3px 0 0 #8573e7; /* color(purple, 300) */
}

.ui-settings-card--embedded {
  padding-block-start: 24px; /* spacing(large) */
  border-block-start: 1px solid #eff0f2; /* color(gray, 100) */
}

.ui-settings-card--embedded.ui-settings-card--over {
  padding-inline-start: 16px; /* spacing(medium) */
  box-shadow: inset 3px 0 0 #8573e7; /* color(purple, 300) */
}

.ui-settings-card--embedded .ui-settings-card__title {
  font-size: 16px; /* font-size(xsmall) */
  line-height: 24px;
}

.ui-settings-card__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px; /* spacing(xsmall) */
  margin-block-end: 4px; /* spacing(2xsmall) */
}

.ui-settings-card__title {
  margin: 0;
  font-size: 20px; /* font-size(small) */
  line-height: 30px;
  font-weight: 500;
  color: #151a25; /* color(gray, 500) */
}

.ui-settings-card__title:focus {
  outline: none;
}

.ui-settings-card__title:focus-visible {
  outline: 2px solid #5956e3; /* color(purple, 500) */
  outline-offset: 2px;
}

.ui-settings-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px; /* spacing(2xsmall) */
  height: 24px;
  padding-inline: 4px 8px; /* spacing(2xsmall) spacing(xsmall) */
  border: 1px solid #dfe1e5; /* color(gray, 200) */
  border-radius: 12px;
  background: #ffffff; /* color(base, white) */
  font-size: 11px; /* font-size(4xsmall) */
  line-height: 16px;
  font-weight: 500;
  color: #62697c; /* color(gray, 400) */
  text-decoration: none;
  white-space: nowrap;
}

a.ui-settings-pill:hover,
a.ui-settings-pill:focus-visible {
  color: #5956e3; /* color(purple, 500) */
  border-color: #cec7f5; /* color(purple, 200) */
}

.ui-settings-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px; /* spacing(medium) */
  align-items: start;
  margin-block-start: 16px; /* spacing(medium) */
}

.ui-settings-panel {
  margin-block-start: 16px; /* spacing(medium) */
  padding: 16px; /* spacing(medium) */
  border: 1px solid #dfe1e5; /* color(gray, 200) */
  border-radius: 8px; /* border-radius(medium) */
}

.ui-settings-panel > .ui-settings-grid {
  margin-block-start: 16px; /* spacing(medium) */
}

.ui-settings-panel__head {
  display: flex;
  align-items: baseline;
  gap: 8px; /* spacing(xsmall) */
}

.ui-settings-panel__title {
  font-size: 16px; /* font-size(xsmall) */
  font-weight: 500;
  color: #151a25; /* color(gray, 500) */
}

.ui-settings-panel__meta {
  font-size: 12px; /* font-size(3xsmall) */
  color: #62697c; /* color(gray, 400) */
}

.ui-settings-card__help {
  display: block;
  margin-block-start: 16px; /* spacing(medium) */
}

@media (max-width: 720px) {
  .ui-settings-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ui-settings-field {
  display: flex;
  flex-direction: column;
  gap: 4px; /* spacing(2xsmall) */
  min-width: 0;
}

.ui-settings-field--wide {
  grid-column: 1 / -1;
}

.ui-settings-field__label {
  display: flex;
  align-items: center;
  gap: 4px; /* spacing(2xsmall) */
  font-size: 12px; /* font-size(3xsmall) */
  line-height: 16px;
  font-weight: 500;
  color: #151a25; /* color(gray, 500) */
}

.ui-settings-field__sublabel {
  flex-basis: 100%;
  font-weight: 400;
  color: #62697c; /* color(gray, 400) */
}

.ui-settings-field__readout {
  font-size: 14px; /* font-size(2xsmall) */
  line-height: 20px;
  color: #151a25; /* color(gray, 500) */
}

.ui-settings-grid--rows {
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
}

.ui-settings-grid--rows > .ui-settings-field {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  column-gap: 16px; /* spacing(medium) */
  row-gap: 8px; /* spacing(xsmall) */
  align-items: start;
  padding-block: 16px; /* spacing(medium) */
  border-block-start: 1px solid #eff0f2; /* color(gray, 100) */
}

.ui-settings-grid--rows > .ui-settings-field:first-child {
  border-block-start: 0;
}

.ui-settings-grid--rows .ui-settings-field__label {
  grid-row: 1 / span 2;
  flex-wrap: wrap;
  font-size: 14px; /* font-size(2xsmall) */
  line-height: 20px;
}

.ui-settings-grid--rows .ui-settings-field__sublabel {
  font-size: 12px; /* font-size(3xsmall) */
  line-height: 18px;
}

.ui-settings-grid--rows > .ui-settings-field > ps-select {
  justify-self: start;
  min-width: 160px;
}

.ui-settings-field__clash {
  padding-inline-start: 8px; /* spacing(xsmall) */
  border-inline-start: 2px solid #8573e7; /* color(purple, 300) */
}

.ui-settings-field__value {
  margin-inline-start: auto;
  font-weight: 400;
}

.ui-settings-tip {
  display: inline-grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 4px; /* border-radius(small) */
  background: none;
  color: #a1a5b0; /* color(gray, 300) */
  cursor: help;
}

.ui-settings-tip:focus-visible {
  outline: 2px solid #5956e3; /* color(purple, 500) */
}

.ui-settings-form {
  display: block;
}

.ui-settings-bar__message--error {
  color: #ff5a5a; /* color(red, 300) */
}

.ui-settings-bar__save--blocked {
  opacity: 0.5;
}

/* Keeps a field focused near the bottom from scrolling in under the fixed snackbar. */
html:has(.ui-settings-form) {
  scroll-padding-bottom: 96px;
}
`;
